import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { Card } from '../../components/ui/Card'
import { Field } from '../../components/ui/Field'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { Pagination } from '../../components/ui/Pagination'
import { useClientPagination } from '../../hooks/useClientPagination'

type Status =
  | 'open'
  | 'awaiting_response'
  | 'in_court'
  | 'closed_won'
  | 'closed_lost'
  | 'closed_settled'

interface LegalCase {
  id: number
  authority: string
  caseNo: string | null
  openedDate: string
  status: Status
  description: string
}

const STATUS_LABELS: Record<Status, string> = {
  open: 'Deschis',
  awaiting_response: 'Așteaptă răspuns',
  in_court: 'În instanță',
  closed_won: 'Închis - câștigat',
  closed_lost: 'Închis - pierdut',
  closed_settled: 'Închis - împăcare',
}
const STATUS_COLORS: Record<Status, string> = {
  open: 'bg-b4l-amber-light text-b4l-amber-dark',
  awaiting_response: 'bg-b4l-bg text-b4l-text-muted',
  in_court: 'bg-b4l-blue-light text-b4l-blue-dark',
  closed_won: 'bg-b4l-green-light text-b4l-green-dark',
  closed_lost: 'bg-b4l-red-light text-b4l-red-dark',
  closed_settled: 'bg-b4l-purple-light text-b4l-purple-dark',
}

export function CazuriLegalePage() {
  const queryClient = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const canWrite = permissions.includes('legal:write')

  const [createOpen, setCreateOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<LegalCase | null>(null)

  const query = useQuery<{ cases: LegalCase[] }>({
    queryKey: ['legal-cases'],
    queryFn: async () => (await api.get<{ cases: LegalCase[] }>('/legal-cases')).data,
  })

  const createMutation = useMutation({
    mutationFn: async (data: Partial<LegalCase>) => (await api.post('/legal-cases', data)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['legal-cases'] })
      toast.success('Caz adăugat')
      setCreateOpen(false)
    },
    onError: showError,
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<LegalCase> }) =>
      (await api.patch(`/legal-cases/${id}`, data)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['legal-cases'] })
      toast.success('Salvat')
      setEditTarget(null)
    },
    onError: showError,
  })

  const statusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: number; status: Status }) =>
      (await api.patch(`/legal-cases/${id}`, { status })).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['legal-cases'] }),
    onError: showError,
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/legal-cases/${id}`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['legal-cases'] })
      toast.success('Șters')
    },
  })

  const cases = useMemo(() => query.data?.cases ?? [], [query.data])
  const pag = useClientPagination(cases, 10)

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Cazuri juridice</h1>
        {canWrite && <Button onClick={() => setCreateOpen(true)}>+ Caz nou</Button>}
      </div>

      <Card description="Registrul cazurilor juridice deschise: autoritate (instanță, ANAF, ANPC etc.), număr de dosar, descriere și stare. Îl văd doar administratorii și contabilitatea.">
        {query.isLoading ? (
          <p className="text-sm text-b4l-text-muted">Se încarcă...</p>
        ) : cases.length === 0 ? (
          <div className="rounded border border-dashed border-b4l-border p-8 text-center text-sm text-b4l-text-muted">
            Niciun caz. {canWrite && 'Apasă „+ Caz nou”.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-b4l-border text-left text-xs uppercase tracking-wide text-b4l-text-muted">
                  <th className="py-2 pr-3 font-medium">Deschis</th>
                  <th className="py-2 pr-3 font-medium">Autoritate</th>
                  <th className="py-2 pr-3 font-medium">Nr. dosar</th>
                  <th className="py-2 pr-3 font-medium">Descriere</th>
                  <th className="py-2 pr-3 font-medium">Stare</th>
                  {canWrite && <th className="py-2 pr-3 font-medium">Acțiuni</th>}
                </tr>
              </thead>
              <tbody>
                {pag.paginated.map((c) => (
                  <tr key={c.id} className="border-b border-b4l-border last:border-0">
                    <td className="py-2 pr-3 text-xs whitespace-nowrap">
                      {new Date(c.openedDate).toLocaleDateString('en-GB')}
                    </td>
                    <td className="py-2 pr-3 max-w-[200px] truncate" title={c.authority}>
                      {c.authority}
                    </td>
                    <td className="py-2 pr-3 font-mono text-[11px]">{c.caseNo ?? '—'}</td>
                    <td className="py-2 pr-3 max-w-[400px] truncate" title={c.description}>
                      {c.description}
                    </td>
                    <td className="py-2 pr-3">
                      {canWrite ? (
                        <select
                          value={c.status}
                          onChange={(e) =>
                            statusMutation.mutate({ id: c.id, status: e.target.value as Status })
                          }
                          className={`rounded px-2 py-0.5 text-[11px] ${STATUS_COLORS[c.status]}`}
                        >
                          {(Object.keys(STATUS_LABELS) as Status[]).map((s) => (
                            <option key={s} value={s}>
                              {STATUS_LABELS[s]}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span
                          className={`rounded px-2 py-0.5 text-[11px] ${STATUS_COLORS[c.status]}`}
                        >
                          {STATUS_LABELS[c.status]}
                        </span>
                      )}
                    </td>
                    {canWrite && (
                      <td className="py-2 pr-3">
                        <div className="flex gap-1">
                          <Button
                            variant="secondary"
                            onClick={() => setEditTarget(c)}
                            className="!px-2 !py-1 !text-xs"
                          >
                            Editează
                          </Button>
                          <Button
                            variant="danger"
                            onClick={() => {
                              if (confirm(`Ștergi cazul ${c.caseNo ?? c.authority}?`))
                                deleteMutation.mutate(c.id)
                            }}
                            className="!px-2 !py-1 !text-xs"
                          >
                            Șterge
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="mt-3">
          <Pagination
            page={pag.page}
            pageSize={pag.pageSize}
            total={pag.total}
            onPageChange={pag.setPage}
            onPageSizeChange={pag.setPageSize}
            itemLabel="cazuri"
          />
        </div>
      </Card>

      {createOpen && (
        <LegalFormModal
          title="Caz juridic nou"
          submitLabel="Adaugă"
          onClose={() => setCreateOpen(false)}
          onSubmit={(d) => createMutation.mutate(d)}
          submitting={createMutation.isPending}
        />
      )}
      {editTarget && (
        <LegalFormModal
          title={`Editează caz ${editTarget.caseNo ?? editTarget.authority}`}
          submitLabel="Salvează"
          initial={editTarget}
          onClose={() => setEditTarget(null)}
          onSubmit={(d) => updateMutation.mutate({ id: editTarget.id, data: d })}
          submitting={updateMutation.isPending}
        />
      )}
    </div>
  )
}

function LegalFormModal({
  title,
  submitLabel,
  initial,
  onClose,
  onSubmit,
  submitting,
}: {
  title: string
  submitLabel: string
  initial?: LegalCase
  onClose: () => void
  onSubmit: (d: Partial<LegalCase>) => void
  submitting: boolean
}) {
  const [authority, setAuthority] = useState(initial?.authority ?? '')
  const [caseNo, setCaseNo] = useState(initial?.caseNo ?? '')
  const [openedDate, setOpenedDate] = useState(
    initial?.openedDate ? initial.openedDate.slice(0, 10) : '',
  )
  const [status, setStatus] = useState<Status>(initial?.status ?? 'open')
  const [description, setDescription] = useState(initial?.description ?? '')

  const submit = () => {
    if (!authority.trim() || !openedDate || !description.trim()) {
      toast.error('Autoritate, dată deschidere și descriere sunt obligatorii')
      return
    }
    onSubmit({
      authority: authority.trim(),
      caseNo: caseNo.trim() || null,
      openedDate: new Date(openedDate).toISOString(),
      status,
      description: description.trim(),
    } as unknown as Partial<LegalCase>)
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Anulează
          </Button>
          <Button onClick={submit} loading={submitting}>
            {submitLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field
          label="Autoritate"
          value={authority}
          onChange={(e) => setAuthority(e.target.value)}
          hint="ex: Judecătoria Sector 1, ANPC, ANAF, Tribunal București"
        />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Nr. dosar" value={caseNo} onChange={(e) => setCaseNo(e.target.value)} />
          <Field
            label="Data deschidere"
            type="date"
            value={openedDate}
            onChange={(e) => setOpenedDate(e.target.value)}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-b4l-text-muted">Stare</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as Status)}
            className="w-full rounded border border-b4l-border px-3 py-2 text-sm"
          >
            {(Object.keys(STATUS_LABELS) as Status[]).map((x) => (
              <option key={x} value={x}>
                {STATUS_LABELS[x]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-b4l-text-muted">Descriere</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            className="w-full rounded border border-b4l-border px-3 py-2 text-sm"
          />
        </div>
      </div>
    </Modal>
  )
}

function showError(err: unknown) {
  const message =
    (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
      ?.message ?? 'Eroare'
  toast.error(message)
}
